'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

/**
 * Refresca los datos del servidor al volver con atrás/adelante del navegador.
 *
 * En el App Router de Next, las navegaciones atrás/adelante siempre reusan el
 * Router Cache del cliente (para que el "atrás" sea instantáneo), y `staleTimes`
 * NO fuerza un refetch en ese caso. Por eso, tras editar y volver atrás, el
 * perfil mostraba datos viejos hasta recargar a mano.
 *
 * Solución: escuchar `popstate` (atrás/adelante) y `pageshow` (restauración
 * desde el bfcache del navegador) y disparar `router.refresh()`, que sí
 * revalida contra el servidor (que ya bustea su cache con revalidateTag al
 * editar). Debe montarse en el layout raíz para que el listener sobreviva a
 * las navegaciones y capture el evento aunque la página destino aún no montó.
 */
export function RefreshOnBackForward() {
	const router = useRouter();

	useEffect(() => {
		// Diferimos al siguiente tick para que el router procese el cambio de URL
		// antes de refrescar, así refresca la ruta destino y no la anterior.
		const refresh = () => window.setTimeout(() => router.refresh(), 0);

		const handlePageShow = (event: PageTransitionEvent) => {
			if (event.persisted) refresh();
		};

		window.addEventListener('popstate', refresh);
		window.addEventListener('pageshow', handlePageShow);

		return () => {
			window.removeEventListener('popstate', refresh);
			window.removeEventListener('pageshow', handlePageShow);
		};
	}, [router]);

	return null;
}
